import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, IconButton } from '@mui/material';
import { CheckCircle, DragIndicator, Close, Notes, AttachFile, ChatBubbleOutline } from '@mui/icons-material';
import { formatDate } from '../utils/dateUtils';
import AttachmentImage from './AttachmentImage';
import { isRasterImage } from '../utils/uploadUtils';
import MarkdownDisplay from './MarkdownDisplay';
import { bugherdCoverUrl, withProxiedDescriptionImages } from './BugherdCardPanel';

const descriptionEchoesName = (name, description) => {
    const text = String(description || '').trim();
    const title = String(name || '').trim().replace(/\.{3}$/, '');
    return !!title && !!text && text.startsWith(title);
};

const Card = styled(Box, { shouldForwardProp: (prop) => prop !== 'done' && prop !== 'canDrag' })(({ theme, done, canDrag }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 1.25),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: done ? theme.palette.action.hover : theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    cursor: canDrag ? 'grab' : 'default',
    opacity: done ? 0.78 : 1,
    height: '100%',
    minHeight: theme.spacing(7),
    boxSizing: 'border-box',
    overflow: 'hidden',
    '&:active': {
        cursor: canDrag ? 'grabbing' : 'default',
    },
    '&:hover .kanban-card-delete': {
        opacity: 1,
    },
}));

const Row = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
}));

const ThumbnailWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    height: 120,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.action.hover,
    pointerEvents: 'none',
}));

const Handle = styled(DragIndicator)(({ theme }) => ({
    color: theme.palette.text.disabled,
    fontSize: theme.typography.size.base,
    marginTop: 2,
    flexShrink: 0,
}));

const Body = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
});

const Name = styled(Typography, { shouldForwardProp: (prop) => prop !== 'done' })(({ theme, done }) => ({
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    textDecoration: done ? 'line-through' : 'none',
    wordBreak: 'break-word',
}));

const Desc = styled(MarkdownDisplay, { shouldForwardProp: (prop) => prop !== 'done' })(({ done }) => ({
    textDecoration: done ? 'line-through' : 'none',
    wordBreak: 'break-word',
}));

const MetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
}));

const MetaIconChip = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    fontSize: theme.typography.caption.fontSize,
}));

const CompletedIcon = styled(CheckCircle)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.success.main,
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(0.25),
    right: theme.spacing(0.25),
    padding: 2,
    opacity: 0,
    transition: 'opacity 120ms ease',
    color: theme.palette.text.secondary,
    '&:hover': {
        color: theme.palette.error.main,
        backgroundColor: theme.palette.action.hover,
    },
}));

const NotesIcon = styled(Notes)(({theme})=>({
    fontSize: theme.typography.size.md
}));

const AttachFileIcon = styled(AttachFile)(({theme}) => ({
    fontSize: theme.typography.size.md
}));

const CommentIcon = styled(ChatBubbleOutline)(({theme}) => ({
    fontSize: theme.typography.size.md
}));

const KanbanCard = React.memo(({ item, taskId, isLocked = false, canDrag = false, canDelete: allowDelete = true, onOpen, onDelete }) => {
    const canDelete = allowDelete && !isLocked && typeof onDelete === 'function' && !item?.__optimistic;
    const canOpen = typeof onOpen === 'function' && !item?.__optimistic;

    const handleCardClick = useCallback((e) => {
        if (!canOpen) return;
        e.stopPropagation();
        onOpen(item);
    }, [canOpen, onOpen, item]);

    const handleDeleteClick = useCallback((e) => {
        e.stopPropagation();
        if (!canDelete) return;
        if (typeof onDelete === 'function') {
            onDelete(item._id);
        }
    }, [canDelete, item, onDelete]);

    if (!item) return null;
    const done = !!item.completed;
    const descriptionRepeatsName = !!item.externalId && descriptionEchoesName(item.name, item.description);
    const hasDescription = !!(item.description && String(item.description).trim()) && !descriptionRepeatsName;
    const visibleAttachments = Array.isArray(item.attachments)
        ? item.attachments.filter((a) => a && typeof a === 'object' && !a.isDeleted)
        : [];
    const attachmentCount = Array.isArray(item.attachments)
        ? item.attachments.filter((a) => a && (!a.isDeleted || typeof a === 'string')).length
        : 0;
    const commentCount = Array.isArray(item.comments)
        ? item.comments.filter((c) => c && !c.isDeleted).length
        : 0;
    const firstImageAttachment = visibleAttachments.find((a) => isRasterImage(a?.mimeType));
    const bugherdCover = item.externalId ? bugherdCoverUrl(taskId, item._id, item.externalData) : '';
    const description = withProxiedDescriptionImages(item.description, taskId, item._id);

    return (
        <Card done={done} canDrag={canDrag} onClick={handleCardClick}>
            {bugherdCover ? (
                <ThumbnailWrapper>
                    <AttachmentImage src={bugherdCover} alt={`BugHerd image for ${item.name || 'this card'}`} loading="lazy" />
                </ThumbnailWrapper>
            ) : firstImageAttachment && (
                <ThumbnailWrapper>
                    <AttachmentImage attachment={firstImageAttachment} alt={firstImageAttachment.name} />
                </ThumbnailWrapper>
            )}
            <Row>
                {canDrag && <Handle fontSize="small" />}
                <Body>
                    <Name done={done} variant="body2">
                        {item.name || 'Untitled'}
                    </Name>
                    {(done && item.completedAt) || hasDescription || attachmentCount > 0 || commentCount > 0 ? (
                        <>
                            {hasDescription && <Desc done={done} content={description} />}
                            <MetaRow>
                                {done && item.completedAt && (
                                    <MetaIconChip>
                                        <CompletedIcon />
                                        <Typography variant="caption">{formatDate(item.completedAt)}</Typography>
                                    </MetaIconChip>
                                )}
                                {hasDescription && (
                                    <MetaIconChip title="Has description">
                                        <NotesIcon/>
                                    </MetaIconChip>
                                )}
                                {attachmentCount > 0 && (
                                    <MetaIconChip title={`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}>
                                        <AttachFileIcon/>
                                        <Typography variant="caption">{attachmentCount}</Typography>
                                    </MetaIconChip>
                                )}
                                {commentCount > 0 && (
                                    <MetaIconChip title={`${commentCount} comment${commentCount === 1 ? '' : 's'}`}>
                                        <CommentIcon/>
                                        <Typography variant="caption">{commentCount}</Typography>
                                    </MetaIconChip>
                                )}
                            </MetaRow>
                        </>
                    ) : null}
                </Body>
            </Row>
            {canDelete && (
                <DeleteButton
                    className="kanban-card-delete"
                    size="small"
                    aria-label="delete card"
                    onClick={handleDeleteClick}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Close fontSize="inherit" />
                </DeleteButton>
            )}
        </Card>
    );
});

export default KanbanCard;
